import { FormEvent, useEffect, useMemo, useState } from 'react'
import { ClinicalInput, Diagnosis, UrineKetones } from './clinical/engine'
import {
  Episode,
  appendObservation,
  canCloseEpisode,
  evaluateEpisode,
  getManualDkaResolutionEligibility,
  makeObservation
} from './clinical/episode'
import TherapyPanel from './TherapyPanel'
import { RuleReferences } from './ReferenceLinks'
import { saveEpisode } from './storage'

const num = (value: FormDataEntryValue | null): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : undefined
}

const urineKetonesValue = (value: FormDataEntryValue | null): UrineKetones | undefined => {
  const parsed = num(value)
  return parsed === 0 || parsed === 0.5 || parsed === 1 || parsed === 2 || parsed === 3 || parsed === 4 ? parsed : undefined
}

const urineKetoneLabel = (value: UrineKetones) => value === 0 ? 'neg' : value === 0.5 ? 'tracce' : `${value}+`
const ketoneDisplay = (input: ClinicalInput) => input.betaHydroxybutyrateMmolL !== undefined
  ? `β ${input.betaHydroxybutyrateMmolL}`
  : input.urineKetones !== undefined
    ? `Ur ${urineKetoneLabel(input.urineKetones)}`
    : '—'
const bloodGasSampleLabel = (input: ClinicalInput) => input.bloodGasSample === 'arterial' ? 'arterioso' : 'venoso'
const bloodGasSampleShort = (input: ClinicalInput) => input.bloodGasSample === 'arterial' ? 'A' : 'V'

const boolChoice = (value: FormDataEntryValue | null): boolean | undefined => value === 'yes' ? true : value === 'no' ? false : undefined

const diagnosisLabels: Record<Diagnosis, string> = {
  DKA: 'DKA',
  EUGLYCEMIC_DKA: 'DKA euglicemica',
  HHS: 'HHS',
  MIXED_DKA_HHS: 'Forma mista DKA/HHS',
  UNCLASSIFIED: 'Non classificato'
}

const toLocalInput = (iso: string) => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const timeLabel = (iso: string) => new Intl.DateTimeFormat('it-IT', {
  hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit'
}).format(new Date(iso))

const elapsed = (start: string, current: string) => {
  const hours = Math.max(0, (new Date(current).getTime() - new Date(start).getTime()) / 3_600_000)
  return hours < 1 ? `T+${Math.round(hours * 60)}m` : `T+${hours.toFixed(hours < 10 ? 1 : 0)}h`
}

const signed = (value?: number, unit = '') => value === undefined ? '—' : `${value > 0 ? '+' : ''}${value}${unit}`
const dueLabel = (state: string) => state === 'due' ? 'DOVUTO' : state === 'soon' ? 'TRA POCO' : state === 'ok' ? 'OK' : 'N/D'
const statusLabel = (status: Episode['status']) => status === 'resolution_candidate' ? 'RISOLUZIONE?' : status === 'resolved' ? 'RISOLTO' : status === 'closed' ? 'CHIUSO' : 'ATTIVO'
const intervalLabel = (hours?: number) => hours === undefined ? 'intervallo n/d' : hours < 1 ? `${Math.round(hours * 60)} min` : `${hours} h`

function RateCard({ label, value, unit, intervalHours }: { label: string; value?: number; unit: string; intervalHours?: number }) {
  return <div className="trend-card"><span>{label}</span><strong>{signed(value, unit)}</strong><small>intervallo {intervalLabel(intervalHours)}</small></div>
}

export default function EpisodePanel({ episode, onChange }: { episode: Episode; onChange: (episode: Episode) => void }) {
  const [now, setNow] = useState(new Date().toISOString())
  const [saved, setSaved] = useState(false)
  const [manualResolutionError, setManualResolutionError] = useState('')

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date().toISOString()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const evaluation = useMemo(() => evaluateEpisode(episode, now), [episode, now])
  const latest = evaluation.latest
  const baseline = episode.observations[0].input
  const initialResult = episode.observations[0].result
  const allAlerts = [...latest.result.alerts, ...evaluation.longitudinalAlerts]
  const indexPatternKind = initialResult.provisionalPattern?.kind
  const hhsContext = episode.diagnosis === 'HHS' || episode.diagnosis === 'MIXED_DKA_HHS' ||
    indexPatternKind === 'HHS_POSSIBLE_NO_KETONES' || indexPatternKind === 'MIXED_POSSIBLE_NO_KETONES'
  const closable = canCloseEpisode(episode)
  const manualResolution = getManualDkaResolutionEligibility(episode)
  const bohbEverAvailable = episode.observations.some((observation) => observation.input.betaHydroxybutyrateMmolL !== undefined)
  const urineKetonesEverAvailable = episode.observations.some((observation) => observation.input.urineKetones !== undefined)
  const indexTitle = episode.diagnosis === 'UNCLASSIFIED' && initialResult.provisionalPattern
    ? initialResult.provisionalPattern.label
    : diagnosisLabels[episode.diagnosis]
  const currentPhenotype = latest.result.diagnosis === 'UNCLASSIFIED'
    ? latest.result.provisionalPattern?.label ?? (latest.result.resolution.dka === true || latest.result.resolution.hhs === true ? 'Crisi non più classificabile come attiva' : 'Non classificato con i dati attuali')
    : diagnosisLabels[latest.result.diagnosis]

  const timelineEvents = useMemo(() => episode.observations.map((observation, index) => {
    const partial: Episode = {
      ...episode,
      status: 'active',
      observations: episode.observations.slice(0, index + 1)
    }
    const historicalEvaluation = evaluateEpisode(partial, observation.collectedAt)
    const alerts = [...observation.result.alerts, ...historicalEvaluation.longitudinalAlerts]
    const unique = alerts.filter((alert, alertIndex) => alerts.findIndex((item) => item.id === alert.id) === alertIndex)
    const events = unique.map((alert) => ({ title: alert.title, level: alert.level }))
    if (historicalEvaluation.status === 'resolution_candidate') events.push({ title: 'Criteri compatibili con risoluzione', level: 'info' })
    return { observationId: observation.id, events }
  }), [episode])

  const persistStatus = async (status: Episode['status']) => {
    if (status === 'closed' && !canCloseEpisode(episode)) return
    const next = { ...episode, status }
    onChange(next)
    await saveEpisode(next)
    if (status === 'closed') window.setTimeout(() => window.location.reload(), 120)
  }

  const confirmConsensusResolution = async () => {
    const next: Episode = {
      ...episode,
      status: 'resolved',
      resolutionConfirmation: { mode: 'consensus_bohb', confirmedAt: new Date().toISOString() }
    }
    onChange(next)
    await saveEpisode(next)
  }

  const confirmManualResolution = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const eligibility = getManualDkaResolutionEligibility(episode)
    const fd = new FormData(event.currentTarget)
    const reason = String(fd.get('manualResolutionReason') || '').trim()
    if (!eligibility.eligible) {
      setManualResolutionError('I dati attuali non soddisfano le condizioni minime per documentare il percorso manuale.')
      return
    }
    if (!reason) {
      setManualResolutionError('Inserire il riferimento al protocollo locale o la motivazione clinica.')
      return
    }
    setManualResolutionError('')
    const next: Episode = {
      ...episode,
      status: 'resolved',
      resolutionConfirmation: {
        mode: 'manual_no_bohb',
        reason,
        confirmedAt: new Date().toISOString()
      }
    }
    onChange(next)
    await saveEpisode(next)
  }

  const addReassessment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    const collectedRaw = String(fd.get('collectedAt') || '')
    if (!collectedRaw) return
    const phValue = num(fd.get('ph'))

    const input: ClinicalInput = {
      knownDiabetes: baseline.knownDiabetes,
      sglt2Inhibitor: baseline.sglt2Inhibitor,
      frailty: baseline.frailty,
      heartFailure: baseline.heartFailure,
      advancedKidneyDisease: baseline.advancedKidneyDisease,
      dialysis: baseline.dialysis,
      pregnancy: baseline.pregnancy,
      weightKg: baseline.weightKg,
      glucoseMgDl: num(fd.get('glucoseMgDl')),
      sodiumMmolL: num(fd.get('sodiumMmolL')),
      potassiumMmolL: num(fd.get('potassiumMmolL')),
      chlorideMmolL: num(fd.get('chlorideMmolL')),
      bicarbonateMmolL: num(fd.get('bicarbonateMmolL')),
      ph: phValue,
      bloodGasSample: phValue !== undefined ? ((fd.get('bloodGasSample') as ClinicalInput['bloodGasSample']) || 'venous') : undefined,
      betaHydroxybutyrateMmolL: num(fd.get('betaHydroxybutyrateMmolL')),
      urineKetones: urineKetonesValue(fd.get('urineKetones')),
      ureaMmolL: num(fd.get('ureaMmolL')),
      measuredOsmolality: hhsContext ? num(fd.get('measuredOsmolality')) : undefined,
      urineOutputMlKgH: hhsContext ? num(fd.get('urineOutputMlKgH')) : undefined,
      cognitionImproved: hhsContext ? boolChoice(fd.get('cognitionImproved')) : undefined,
      mentalStatus: (fd.get('mentalStatus') as ClinicalInput['mentalStatus']) || 'unknown'
    }

    const treatment = {
      insulinUnitsHour: num(fd.get('insulinUnitsHour')),
      fluidMlHour: num(fd.get('fluidMlHour'))
    }

    const hasClinicalData = Object.entries(input).some(([key, value]) =>
      !['knownDiabetes', 'sglt2Inhibitor', 'frailty', 'heartFailure', 'advancedKidneyDisease', 'dialysis', 'pregnancy', 'weightKg', 'mentalStatus', 'bloodGasSample'].includes(key) && value !== undefined
    )
    const hasTreatment = treatment.insulinUnitsHour !== undefined || treatment.fluidMlHour !== undefined
    if (!hasClinicalData && !hasTreatment) return

    const observation = makeObservation(input, new Date(collectedRaw).toISOString(), treatment)
    const next = appendObservation(episode, observation)
    onChange(next)
    await saveEpisode(next)
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1800)
  }

  return (
    <section className="episode-panel panel">
      <div className="episode-header">
        <div className="diagnosis-context">
          <p className="step">{episode.diagnosis === 'UNCLASSIFIED' && initialResult.provisionalPattern ? 'Pattern indice · non confermato' : 'Diagnosi indice'}</p>
          <h2>{indexTitle}</h2>
          <p className="current-phenotype">Quadro biochimico attuale: <strong>{currentPhenotype}</strong></p>
          <p className="muted">Avviato {timeLabel(episode.startedAt)} · {episode.observations.length} rilevazioni</p>
        </div>
        <span className={`episode-status ${evaluation.status}`}>{statusLabel(evaluation.status)}</span>
      </div>

      <RuleReferences ids={['diagnosis']} />

      {evaluation.status === 'resolution_candidate' && (
        <div className="resolution-banner resolution-action">
          <div><strong>Criteri consensus compatibili con risoluzione.</strong><span> β-OHB disponibile: confermare clinicamente prima della transizione.</span></div>
          <button type="button" onClick={confirmConsensusResolution}>Conferma risoluzione</button>
        </div>
      )}

      {manualResolution.eligible && (
        <form className="manual-resolution" onSubmit={confirmManualResolution}>
          <div>
            <strong>β-OHB non disponibile: risoluzione non automatizzabile.</strong>
            <p>pH/HCO₃⁻ sono rientrati e la glicemia è &lt;200 mg/dL, ma chetonuria e anion gap non vengono usati come criteri di risoluzione. È possibile documentare una decisione clinica secondo protocollo locale.</p>
          </div>
          <label>
            <span>Protocollo locale / motivazione clinica</span>
            <input name="manualResolutionReason" type="text" placeholder="es. criteri locali + quadro clinico favorevole" />
          </label>
          <button type="submit">Documenta risoluzione manuale</button>
          {manualResolutionError && <p className="manual-resolution-error">{manualResolutionError}</p>}
        </form>
      )}

      {episode.status === 'resolved' && (
        <div className={`resolution-banner resolution-action ${closable ? '' : 'close-gated'}`}>
          <div>
            <strong>Risoluzione confermata.</strong>
            {episode.resolutionConfirmation?.mode === 'manual_no_bohb' && <span> Percorso manuale per β-OHB non disponibile documentato.</span>}
            <span>{closable ? ' Transizione/percorso alternativo documentato: l’episodio può essere chiuso.' : ' Documentare la transizione EV→SC o un percorso alternativo prima della chiusura.'}</span>
          </div>
          {closable ? <button type="button" onClick={() => persistStatus('closed')}>Chiudi episodio</button> : <span className="close-lock">Chiusura non ancora disponibile</span>}
        </div>
      )}

      {(evaluation.status === 'resolution_candidate' || manualResolution.eligible || episode.status === 'resolved') && <RuleReferences ids={['resolution', 'ketones']} />}

      <p className="section-label current-label">Stato attuale</p>
      <div className="current-state-grid">
        <div><span>Glicemia</span><strong>{latest.input.glucoseMgDl ?? '—'}</strong><small>mg/dL</small></div>
        <div><span>Na</span><strong>{latest.input.sodiumMmolL ?? '—'}</strong><small>mmol/L</small></div>
        <div><span>K</span><strong>{latest.input.potassiumMmolL ?? '—'}</strong><small>mmol/L</small></div>
        <div><span>Chetoni</span><strong>{ketoneDisplay(latest.input)}</strong><small>{latest.input.betaHydroxybutyrateMmolL !== undefined ? 'mmol/L · β-OHB' : latest.input.urineKetones !== undefined ? 'chetonuria' : 'non disponibili'}</small></div>
        <div><span>pH</span><strong>{latest.input.ph ?? '—'}</strong><small>{latest.input.ph !== undefined ? bloodGasSampleLabel(latest.input) : 'campione n/d'}</small></div>
        <div><span>HCO₃⁻</span><strong>{latest.input.bicarbonateMmolL ?? '—'}</strong><small>mmol/L</small></div>
        <div><span>Osm eff.</span><strong>{latest.result.calculations.effectiveOsmolality ?? '—'}</strong><small>mOsm/kg</small></div>
      </div>

      <div className="monitor-grid">
        <div className={`monitor-card ${evaluation.monitoring.glucose}`}><span>Glicemia</span><strong>{dueLabel(evaluation.monitoring.glucose)}</strong><small>ultima {evaluation.monitoring.glucoseAgeHours ?? '—'} h fa</small></div>
        <div className={`monitor-card ${evaluation.monitoring.metabolicPanel}`}><span>Elettroliti / pH</span><strong>{dueLabel(evaluation.monitoring.metabolicPanel)}</strong><small>ultimo {evaluation.monitoring.metabolicAgeHours ?? '—'} h fa</small></div>
        <div className={`monitor-card ${bohbEverAvailable ? evaluation.monitoring.ketones : 'unknown'}`}><span>β-OHB</span><strong>{bohbEverAvailable ? dueLabel(evaluation.monitoring.ketones) : 'NON DISP.'}</strong><small>{bohbEverAvailable ? `ultimo ${evaluation.monitoring.ketoneAgeHours ?? '—'} h fa` : urineKetonesEverAvailable ? 'chetonuria disponibile come fallback diagnostico' : 'nessun metodo chetonico documentato'}</small></div>
      </div>
      <RuleReferences ids={['ketones']} />

      <div className="trend-heading"><p className="section-label">Δ/ora · ultimo intervallo utile</p></div>
      <div className="trend-grid">
        <RateCard label="Glicemia" value={evaluation.rates.glucoseMgDlHour} unit=" mg/dL/h" intervalHours={evaluation.rates.glucoseIntervalHours} />
        <RateCard label="Na" value={evaluation.rates.sodiumMmolLHour} unit=" mmol/L/h" intervalHours={evaluation.rates.sodiumIntervalHours} />
        <RateCard label="Osm eff." value={evaluation.rates.effectiveOsmolalityHour} unit=" mOsm/kg/h" intervalHours={evaluation.rates.effectiveOsmolalityIntervalHours} />
        <RateCard label="β-OHB" value={evaluation.rates.betaHydroxybutyrateHour} unit=" mmol/L/h" intervalHours={evaluation.rates.betaHydroxybutyrateIntervalHours} />
      </div>
      {hhsContext && <RuleReferences ids={['hhs_guardrails']} />}

      {!!allAlerts.length && episode.status !== 'resolved' && (
        <div className="episode-alerts">
          {allAlerts.map((alert) => (
            <article className={`alert ${alert.level}`} key={`${latest.id}-${alert.id}`}>
              <strong>{alert.title}</strong>
              <details className="why"><summary>Perché?</summary><p>{alert.message}</p></details>
            </article>
          ))}
        </div>
      )}

      <TherapyPanel episode={episode} onChange={onChange} />

      {episode.status !== 'resolved' && episode.status !== 'closed' && (
        <details className="reassessment" open>
          <summary>+ Nuova rivalutazione</summary>
          <form key={episode.observations.length} onSubmit={addReassessment}>
            <div className="reassess-grid">
              <label><span>Data/ora</span><input type="datetime-local" name="collectedAt" defaultValue={toLocalInput(new Date().toISOString())} required /></label>
              <label><span>Glicemia</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="glucoseMgDl" /><small>mg/dL</small></div></label>
              <label><span>Na</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="sodiumMmolL" /><small>mmol/L</small></div></label>
              <label><span>K</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="potassiumMmolL" /><small>mmol/L</small></div></label>
              <label><span>Cl</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="chlorideMmolL" /><small>mmol/L</small></div></label>
              <label><span>HCO₃⁻</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="bicarbonateMmolL" /><small>mmol/L</small></div></label>
              <label><span>pH</span><input type="number" step="0.01" inputMode="decimal" name="ph" /></label>
              <label><span>Campione pH</span><select name="bloodGasSample" defaultValue={latest.input.bloodGasSample ?? baseline.bloodGasSample ?? 'venous'}><option value="venous">Venoso</option><option value="arterial">Arterioso</option></select></label>
              <label><span>β-OHB</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="betaHydroxybutyrateMmolL" /><small>mmol/L</small></div></label>
              <label><span>Chetonuria</span><select name="urineKetones" defaultValue=""><option value="">Non disponibile / non eseguita</option><option value="0">Negativi</option><option value="0.5">Tracce</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option><option value="4">4+</option></select></label>
              <label><span>Urea</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="ureaMmolL" /><small>mmol/L</small></div></label>
              {hhsContext && <label><span>Osm misurata</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="measuredOsmolality" /><small>mOsm/kg</small></div></label>}
              {hhsContext && <label><span>Diuresi</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="urineOutputMlKgH" /><small>mL/kg/h</small></div></label>}
              {hhsContext && <label><span>Miglioramento cognitivo</span><select name="cognitionImproved" defaultValue=""><option value="">Non valutato</option><option value="yes">Sì</option><option value="no">No</option></select></label>}
              <label><span>Insulina attuale</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="insulinUnitsHour" /><small>U/h</small></div></label>
              <label><span>Fluidi attuali</span><div className="input-wrap"><input type="number" step="any" inputMode="decimal" name="fluidMlHour" /><small>mL/h</small></div></label>
              <label><span>Stato neurologico</span><select name="mentalStatus" defaultValue="unknown"><option value="unknown">Non specificato</option><option value="alert">Vigile</option><option value="drowsy">Sonnolento</option><option value="stupor">Stupor</option><option value="coma">Coma</option></select></label>
            </div>
            <button className="primary" type="submit">Aggiungi alla timeline</button>
            {saved && <p className="saved">Rivalutazione salvata localmente.</p>}
          </form>
        </details>
      )}

      <div className="timeline-wrap">
        <div className="timeline-title"><h3>Timeline</h3><small>scorri orizzontalmente su smartphone</small></div>
        <div className="table-scroll">
          <table className="timeline-table">
            <thead><tr><th>Tempo</th><th>Ora</th><th>Glu</th><th>Na</th><th>K</th><th>Chetoni</th><th>pH (V/A)</th><th>HCO₃</th><th>Osm eff.</th><th>Insulina</th><th>Fluidi</th><th>Eventi</th></tr></thead>
            <tbody>
              {episode.observations.map((obs) => {
                const historical = timelineEvents.find((item) => item.observationId === obs.id)
                return (
                  <tr key={obs.id}>
                    <td>{elapsed(episode.startedAt, obs.collectedAt)}</td><td>{timeLabel(obs.collectedAt)}</td><td>{obs.input.glucoseMgDl ?? '—'}</td><td>{obs.input.sodiumMmolL ?? '—'}</td><td>{obs.input.potassiumMmolL ?? '—'}</td><td>{ketoneDisplay(obs.input)}</td><td>{obs.input.ph !== undefined ? `${obs.input.ph} ${bloodGasSampleShort(obs.input)}` : '—'}</td><td>{obs.input.bicarbonateMmolL ?? '—'}</td><td>{obs.result.calculations.effectiveOsmolality ?? '—'}</td><td>{obs.treatment?.insulinUnitsHour !== undefined ? `${obs.treatment.insulinUnitsHour} U/h` : '—'}</td><td>{obs.treatment?.fluidMlHour !== undefined ? `${obs.treatment.fluidMlHour} mL/h` : '—'}</td>
                    <td className="event-cell">{historical?.events.length ? historical.events.map((event, index) => <span className={`event-chip ${event.level}`} key={`${obs.id}-${index}`}>{event.title}</span>) : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
